import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch } from "react-router";

import {
	publishVersion,
	loadCreativeVersions,
	deleteVersions,
	sendForAutoClassification,
	socialPublishVersion,
	socialUnpublishVersion
} from "reducers/CreativeVersions";
import { loadCreative } from "reducers/CreativeDetails";
import { confirmDeleteVersion } from "reducers/Confirm";
import { getRemainingCredits } from "selectors/Intelligence";
import { canDeleteVersion } from "utils/VersionActions";
import { hasPermission, isSocialCreative } from "utils/common";
import { CV, DECISIONING_TYPES, NON_AUTOCLASSIFIABLE_ADTYPES, VERSION_STATUS } from "utils/constants";

import { Confirm } from "rhombus-react";
import ActionMenu, { BasicAction, LinkAction } from "widgets/ActionMenu";
import { isApproved } from "utils/CreativeActions";

const VERSION_DOMAIN = "Version Library";

export const renameAction = {
	shouldDisplay({ user, version }){
		let userCanRename = hasPermission(user, VERSION_DOMAIN, "Rename version");
		let { name, status } = version || {};

		return userCanRename && (name !== "default") && (status !== VERSION_STATUS.LIVE);
	},
	component({ version }){
		let { url } = useRouteMatch();
		return (
			<LinkAction
				iconName = "edit"
				name = "Rename Version"
				path = {`${url}${version.id}/modal/rename-version/`}
			/>
		);
	},
};

export const socialPublishAction = {
	shouldDisplay({ user, creative, version }) {
		return [
			isSocialCreative(creative),
			version.status === VERSION_STATUS.UNPUBLISHED,
			hasPermission(user, CV, "Manage Version Library"),
		].every(item => item);
	},
	component({ version }) {
		const dispatch = useDispatch();

		const handleClick = () => {
			dispatch(socialPublishVersion([version.id]));
		};

		return (
			<BasicAction
				iconName="upload"
				name="Publish"
				handleClick={handleClick}
			/>
		);
	},
};

export const socialUnpublishAction = {
	shouldDisplay({ user, creative, version }) {
		return [
			isSocialCreative(creative),
			version.status === VERSION_STATUS.LIVE,
			hasPermission(user, CV, "Manage Version Library"),
		].every(item => item);
	},
	component({ version }) {
		const dispatch = useDispatch();

		const handleClick = () => {
			dispatch(socialUnpublishVersion([version.id]));
		};

		return (
			<BasicAction
				iconName="stop"
				name="Unpublish"
				handleClick={handleClick}
			/>
		);
	},
};

export const publishAction = {
	shouldDisplay({ user, creative, version }){
		let { decisioningType, decisioningName } = creative || {};
		let { name: versionName } = version || {};

		let userCanPublish = hasPermission(user, VERSION_DOMAIN, "Publish version");
		let isVersionCurrentlyPublished = (decisioningType === DECISIONING_TYPES.PUBLISHED_VERSION) && (decisioningName === versionName);

		return userCanPublish && !isVersionCurrentlyPublished && !isSocialCreative(creative) ;
	},
	component({ libraryId, creative, version }){
		const dispatch = useDispatch();
		const creativeId = creative.id;

		function closeConfirm(){
			return dispatch(Confirm.closeConfirmAction());
		}

		function publish(){
			// ConfirmModal requires that the onCommit function it's given returns a promise
			return dispatch(publishVersion(libraryId, creativeId, version.id))
				.then((response) => {
					if(response?.success){
						/*
							After publishing, multiple Version rows will have their `status` updated, and the Creative
						 	will have its decisioning fields updated, so we have to refetch
						*/
						dispatch(loadCreativeVersions(libraryId, creativeId));
						dispatch(loadCreative(creativeId));
						closeConfirm();
					}
				});
		}

		function handleClick(){
			/*
				If the Creative has a decisioningType other than Published Version, we should show a
				confirmation modal.  Otherwise, just change which version is published.
			*/
			let { decisioningType } = creative;
			if(decisioningType === DECISIONING_TYPES.PUBLISHED_VERSION){
				return publish();
			}

			let confirmPublishProps = {
				title: "Publish Version",
				desc: (
					<p>
						Are you sure you want to publish version <b>“{version.name}”</b>?<br />
						This will remove any decisioning applied to other versions for this creative.
					</p>
				),
				commitLabel: "Publish Version",
				commitTheme: "green",
				onCommit: publish,
				onClose: closeConfirm,
			};

			dispatch(Confirm.openConfirmAction(confirmPublishProps));
		}

		return (
			<BasicAction
				iconName = "upload"
				name = "Publish"
				handleClick = {handleClick}
			/>
		);
	},
};

export const classifyAction = {
	icon: "fas fa-tags",
	name: "Send for Auto-Classification",
	shouldDisplay({ creative, version, isAutoClassificationEnabled }){
		const { approval, adType } = creative;
		return [
			isAutoClassificationEnabled,
			version.status !== VERSION_STATUS.UNPUBLISHED,
			isApproved(approval),
			!NON_AUTOCLASSIFIABLE_ADTYPES.includes(adType),
		].every(condition => condition);
	},
	component: ({ creative, version }) => {
		const dispatch = useDispatch();
		const credits = useSelector(getRemainingCredits);
		function handleClick(){
			dispatch(sendForAutoClassification(creative.id, [version.id]));
		}
		return (
			<BasicAction
				iconName = "fas fa-tags"
				name = "Send for Auto-Classification"
				isDisabled = {credits === 0}
				handleClick = {handleClick}
			/>
		);
	},
};

export const deleteAction = {
	shouldDisplay({ user, version }){
		let userCanDelete = hasPermission(user, VERSION_DOMAIN, "Delete version");

		return userCanDelete && canDeleteVersion(version);
	},
	component({ libraryId, creative, version }){
		const dispatch = useDispatch();
		function handleClick(){
			dispatch(confirmDeleteVersion(
				deleteVersions,
				{
					libraryId,
					creativeId: creative.id,
					versionIds: [version.id],
				}
			));
		}

		return (
			<BasicAction
				iconName = "delete"
				name = "Delete Version"
				handleClick = {handleClick}
			/>
		);
	},
};

export default function VersionEllipsisActions(props){
	let actionLists = [
		[
			renameAction,
			publishAction,
			socialPublishAction,
			socialUnpublishAction,
		],
		[
			classifyAction,
		],
		[
			deleteAction
		]
	];
	return (
		<ActionMenu
			key = "version-additional-actions"
			title = "Additional Actions"
			actionLists = {actionLists}
			actionContext = {props} // See propTypes for expected data
		/>
	);
}

VersionEllipsisActions.displayName = "VersionEllipsisActions";
VersionEllipsisActions.propTypes = {
	user: PropTypes.object,
	libraryId: PropTypes.number,
	creative: PropTypes.shape({
		id: PropTypes.number,
		decisioningType: PropTypes.string,
		decisioningName: PropTypes.string,
	}),
	version: PropTypes.shape({
		id: PropTypes.number.isRequired,
		name: PropTypes.string.isRequired,
	}),
};
